import { useMemo } from 'react';
import { useUserRoles } from './useUserRoles';
import { 
  useCanAccessItemsInProject, 
  useCanEditItemsInProject, 
  useCanDeleteItemsInProject,
  useCanAccessSpecsInProject,
  useCanEditSpecsInProject,
  useCanDeleteSpecsInProject,
  useCanAccessProcurementInProject,
  useCanEditProcurementInProject,
  useCanDeleteProcurementInProject,
  useCanAccessStakeholdersInProject,
  useCanEditStakeholdersInProject,
  useCanDeleteStakeholdersInProject,
  useCanAccessSubconsInProject,
  useCanEditSubconsInProject,
  useCanDeleteSubconsInProject
} from './useUnifiedPermissions';

/**
 * Resource-specific Project Permission Wrappers
 * 
 * ✅ Phase C: Refactored to use unified hooks
 * 
 * These hooks delegate to useUnifiedPermissions (useGate) to enforce AND logic:
 * 1. Global permission (e.g., ITEMS_ACCESS)
 * 2. Project-level permission (project_user_permissions.can_view/can_edit/can_delete)
 * 
 * Benefits:
 * - No code duplication
 * - Single source of truth for AND logic
 * - Consistent with RLS policies
 * - Easier to maintain and test
 */

interface ResourceProjectPermissions {
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  isLoading: boolean;
}

/**
 * Items (Timeline) project permissions
 * Requires: ITEMS_ACCESS + project permissions
 */
export const useItemsProjectPermissions = (projectId: string): ResourceProjectPermissions => {
  const { data: userRoles } = useUserRoles();
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
  
  const { allowed: canView, isLoading: viewLoading } = useCanAccessItemsInProject(projectId);
  const { allowed: canEdit, isLoading: editLoading } = useCanEditItemsInProject(projectId);
  const { allowed: canDelete, isLoading: deleteLoading } = useCanDeleteItemsInProject(projectId);
  
  return useMemo(() => {
    // Admin bypass
    if (isAdmin) {
      return {
        can_view: true,
        can_edit: true,
        can_delete: true,
        isLoading: false
      };
    }
    
    return {
      can_view: canView,
      can_edit: canEdit,
      can_delete: canDelete,
      isLoading: viewLoading || editLoading || deleteLoading
    };
  }, [canView, canEdit, canDelete, viewLoading, editLoading, deleteLoading, isAdmin]);
};

/**
 * Specs project permissions
 * Requires: SPECS_ACCESS + project permissions
 */
export const useSpecsProjectPermissions = (projectId: string): ResourceProjectPermissions => {
  const { data: userRoles } = useUserRoles();
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
  
  const { allowed: canView, isLoading: viewLoading } = useCanAccessSpecsInProject(projectId);
  const { allowed: canEdit, isLoading: editLoading } = useCanEditSpecsInProject(projectId);
  const { allowed: canDelete, isLoading: deleteLoading } = useCanDeleteSpecsInProject(projectId);
  
  return useMemo(() => {
    if (isAdmin) {
      return {
        can_view: true,
        can_edit: true,
        can_delete: true,
        isLoading: false
      };
    }
    
    return {
      can_view: canView,
      can_edit: canEdit,
      can_delete: canDelete,
      isLoading: viewLoading || editLoading || deleteLoading
    };
  }, [canView, canEdit, canDelete, viewLoading, editLoading, deleteLoading, isAdmin]);
};

/**
 * Procurement project permissions
 * Requires: PROCUREMENT_ACCESS + project permissions
 */
export const useProcurementProjectPermissions = (projectId: string): ResourceProjectPermissions => {
  const { data: userRoles } = useUserRoles();
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
  
  const { allowed: canView, isLoading: viewLoading } = useCanAccessProcurementInProject(projectId);
  const { allowed: canEdit, isLoading: editLoading } = useCanEditProcurementInProject(projectId);
  const { allowed: canDelete, isLoading: deleteLoading } = useCanDeleteProcurementInProject(projectId);
  
  return useMemo(() => {
    if (isAdmin) {
      return {
        can_view: true,
        can_edit: true,
        can_delete: true,
        isLoading: false
      };
    }
    
    return {
      can_view: canView,
      can_edit: canEdit,
      can_delete: canDelete,
      isLoading: viewLoading || editLoading || deleteLoading
    };
  }, [canView, canEdit, canDelete, viewLoading, editLoading, deleteLoading, isAdmin]);
};

/**
 * Stakeholders project permissions
 * Requires: STAKEHOLDERS_ACCESS + project permissions
 */
export const useStakeholdersProjectPermissions = (projectId: string): ResourceProjectPermissions => {
  const { data: userRoles } = useUserRoles();
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
  
  const { allowed: canView, isLoading: viewLoading } = useCanAccessStakeholdersInProject(projectId);
  const { allowed: canEdit, isLoading: editLoading } = useCanEditStakeholdersInProject(projectId);
  const { allowed: canDelete, isLoading: deleteLoading } = useCanDeleteStakeholdersInProject(projectId);
  
  return useMemo(() => {
    if (isAdmin) {
      return {
        can_view: true,
        can_edit: true,
        can_delete: true,
        isLoading: false
      };
    }
    
    return {
      can_view: canView,
      can_edit: canEdit,
      can_delete: canDelete,
      isLoading: viewLoading || editLoading || deleteLoading
    };
  }, [canView, canEdit, canDelete, viewLoading, editLoading, deleteLoading, isAdmin]);
};

/**
 * Subcons project permissions
 * Requires: SUBCONS_ACCESS + project permissions
 */
export const useSubconsProjectPermissions = (projectId: string): ResourceProjectPermissions => {
  const { data: userRoles } = useUserRoles();
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
  
  const { allowed: canView, isLoading: viewLoading } = useCanAccessSubconsInProject(projectId);
  const { allowed: canEdit, isLoading: editLoading } = useCanEditSubconsInProject(projectId);
  const { allowed: canDelete, isLoading: deleteLoading } = useCanDeleteSubconsInProject(projectId);
  
  return useMemo(() => {
    if (isAdmin) {
      return {
        can_view: true,
        can_edit: true,
        can_delete: true,
        isLoading: false
      };
    }
    
    return {
      can_view: canView,
      can_edit: canEdit,
      can_delete: canDelete,
      isLoading: viewLoading || editLoading || deleteLoading
    };
  }, [canView, canEdit, canDelete, viewLoading, editLoading, deleteLoading, isAdmin]);
};
